import cashierSelectors from 'features/cashier/services/selectors';
import { ICustomerServicePackage, IUseCustomerPackageSession } from 'features/cashier/services/types/customer-service-package';
import { getUsableSessions } from 'features/cashier/services/use-package-ticket-item';
import { useEffect, useState } from 'react';
import shopSelectors from 'services/shop/selectors';
import { formatCurrency } from 'utils/formatCurrency';
import PackageDetailRow from './package-detail-row';
import { BODY_ZOOM, BodyStyled, CloseButton, CustomerCard, ModalFooter, ModalStyled, OkButton } from './styles';

type Props = {
  visible: boolean;
  packages: ICustomerServicePackage[];
  onClose: () => void;
  onUseSessions: (sessions: IUseCustomerPackageSession[]) => void;
};

/** Chữ cái đầu của tối đa 2 từ đầu tiên trong tên khách, dùng cho avatar */
const getInitials = (name?: string | null) => {
  const words = (name || '').trim().split(/\s+/).filter(Boolean);
  if (!words.length) return '?';
  return words.slice(0, 2).map(word => word[0]).join('').toUpperCase();
};

/** Modal chi tiết gói liệu trình của khách, chọn số buổi dùng cho ticket hiện tại */
const PackagesDetailModal = ({ visible, packages, onClose, onUseSessions }: Props) => {
  const customer = cashierSelectors.getSelectedCustomer();
  const selectedItems = cashierSelectors.getSelectedItems();
  const loyaltySetting = shopSelectors.data.getLoyaltySetting();

  // số buổi chọn cho từng gói, chỉ sống trong modal; chỉ khi bấm OK mới đụng tới ticket
  const [quantities, setQuantities] = useState<Record<string, number>>({});

  // destroyOnClose chỉ huỷ children, component này vẫn mounted nên phải tự dọn mỗi lần mở/đóng
  useEffect(() => {
    setQuantities({});
  }, [visible]);

  const usableByPackage = packages.reduce<Record<string, number>>((acc, pkg) => {
    acc[pkg.id] = getUsableSessions(selectedItems, pkg);
    return acc;
  }, {});

  const availableSessions = packages.reduce((total, pkg) => total + (usableByPackage[pkg.id] || 0), 0);
  const totalSelected = packages.reduce((total, pkg) => total + (quantities[pkg.id] || 0), 0);
  // quy đổi điểm sang tiền theo đúng cách modal stamp đang tính
  const dollar = loyaltySetting?.oneDollarToPoint
    ? +(customer?.point || 0) / loyaltySetting.oneDollarToPoint
    : 0;

  const handleChange = (packageId: string, quantity: number) => {
    const max = usableByPackage[packageId] || 0;
    setQuantities(prev => ({ ...prev, [packageId]: Math.min(Math.max(quantity, 0), max) }));
  };

  const handleOk = () => {
    if (totalSelected <= 0) return;
    const sessions = packages
      .map(data => ({ data, quantity: quantities[data.id] || 0 }))
      .filter(o => o.quantity > 0);
    // đóng trước rồi mới dispatch để lần bấm thứ hai không kịp đẩy item vào ticket
    onClose();
    onUseSessions(sessions);
  };

  return (
    <ModalStyled
      visible={visible}
      onClose={onClose}
      modalTitle='PACKAGES DETAIL'
      width={1000 * BODY_ZOOM}
      noneBodyStyle
      destroyOnClose
      footer={
        <ModalFooter>
          <CloseButton type='button' onClick={onClose}>Close</CloseButton>
          <OkButton type='button' disabled={totalSelected <= 0} onClick={handleOk}>OK</OkButton>
        </ModalFooter>
      }
    >
      <BodyStyled>
        <CustomerCard>
          <div className='customer-info'>
            <div className='avatar'>{getInitials(customer?.customerName)}</div>
            <div>
              <p className='customer-name'>{customer?.customerName || 'UNKNOWN CUSTOMER'}</p>
              {loyaltySetting?.active && (
                <p className='customer-point'>{customer?.point || 0} POINTS = {formatCurrency(dollar)}</p>
              )}
            </div>
          </div>
          <div className='available-sessions'>
            <p className='available-value'>{availableSessions}</p>
            <p className='available-label'>Sessions available</p>
          </div>
        </CustomerCard>

        {packages.map(data => (
          <PackageDetailRow
            key={data.id}
            data={data}
            usableQuantity={usableByPackage[data.id] || 0}
            value={quantities[data.id] || 0}
            onChange={quantity => handleChange(data.id, quantity)}
          />
        ))}
      </BodyStyled>
    </ModalStyled>
  );
};

export default PackagesDetailModal;
